// src/paths/PathGraph.tsx — the /paths/:id graph, ported from Francis's
// PyPath mockup (pypath-1.html) with the locked v1 decisions applied:
// no XP anywhere, est-minutes as the only effort signal, and the graph is
// READ-ONLY — path chat (step 4, PathChatPanel) is the ONLY mutation
// channel. The drawer's mockup buttons (skip / mark done / another way)
// live on as canned chat messages, per the locked design.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { PathNode, PathResponse, PathSurface } from '../types/paths';
import PathChatPanel from './PathChatPanel';
import {
  layoutPath,
  statusOf,
  formatMinutes,
  radiusOf,
  PROJECT_R,
} from './layout';
import './paths.css';

const SURFACE_META: Record<PathSurface, { icon: string; label: string }> = {
  chat: { icon: '💬', label: 'Chat' },
  bookmate: { icon: '📖', label: 'Bookmate' },
  workflow: { icon: '⚡', label: 'Workflow' },
  capstone: { icon: '🏗️', label: 'Capstone' },
  mlmate: { icon: '🤖', label: 'MLmate' },
};

const LEVEL_TITLE: Record<string, string> = {
  beginner: 'Beginner',
  intermediate: 'Intermediate',
  advanced: 'Advanced',
  project: 'Project',
};

// Status → node/edge colors (from the mockup's ST map; skipped/learning
// return with path-chat + learning_events). No locked state by design
// (Francis 2026-08-08): depends_on edges guide, they never gate.
const ST = {
  completed: { c: '#34d399', g: 'rgba(52,211,153,.55)', label: 'Completed' },
  available: { c: '#54627f', g: 'rgba(84,98,127,.30)', label: 'To learn' },
};

const PROJ_C = '#c084fc';
const PROJ_G = 'rgba(192,132,252,.6)';

const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));

const ICONS = {
  check: (
    <svg viewBox="0 0 24 24">
      <path d="M4.5 12.5l5 5L19.5 7" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  close: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  ),
};

// Deep link for Start → (the Help mechanism): prompt prefilled via ?prompt=,
// plus ?path&node so the step-3 learning_events write path can attribute the
// launch. MLmate has no prompt input — plain link.
// Clicking Start → IS the consent (Francis 2026-09-02): Chat auto-sends on
// path&node presence; Bookmate needs its existing &autosubmit=1 flag to start
// generation immediately. Capstone deliberately stays manual — its intent
// screen is where the user confirms before a container spins up.
function deepLink(pathId: string, node: PathNode): string {
  if (node.surface === 'mlmate') return '/mlmate';
  const base: Record<Exclude<PathSurface, 'mlmate'>, string> = {
    chat: '/codemate',
    bookmate: '/bookmate',
    workflow: '/workflow',
    capstone: '/capstone',
  };
  const q = `prompt=${encodeURIComponent(node.prompt)}&path=${encodeURIComponent(pathId)}&node=${encodeURIComponent(node.id)}`;
  const autosubmit = node.surface === 'bookmate' ? '&autosubmit=1' : '';
  return `${base[node.surface]}?${q}${autosubmit}`;
}

interface Props {
  path: PathResponse;
  // Path chat returns the fresh full path after every applied op — the
  // parent owns the path state so the whole graph repaints from it.
  onPathUpdate: (path: PathResponse) => void;
}

const PathGraph: React.FC<Props> = ({ path, onPathUpdate }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isStarter = user?.subscription_tier === 'starter';

  const layout = useMemo(() => layoutPath(path.nodes), [path.nodes]);
  const { positioned, byId, lanes, world, edges } = layout;

  // completed_nodes = server-side union of chat-op overrides + learning_events
  // completions (step 3). Overrides kept as fallback for stale cached payloads.
  const completed = useMemo(
    () => new Set([...(path.completed_nodes ?? []), ...path.completed_overrides]),
    [path.completed_nodes, path.completed_overrides]
  );
  const statuses = useMemo(() => {
    const m: Record<string, ReturnType<typeof statusOf>> = {};
    path.nodes.forEach((n) => { m[n.id] = statusOf(n, completed); });
    return m;
  }, [path.nodes, completed]);

  // The suggested next step: first available node in layout order — it pulses.
  const nextId = useMemo(
    () => positioned.find((p) => statuses[p.node.id] === 'available')?.node.id ?? null,
    [positioned, statuses]
  );

  const nodeById = useMemo(() => new Map(path.nodes.map((n) => [n.id, n])), [path.nodes]);
  const outgoing = useMemo(() => {
    const m: Record<string, string[]> = {};
    edges.forEach(([a, b]) => { (m[a] = m[a] || []).push(b); });
    return m;
  }, [edges]);

  const [selected, setSelected] = useState<string | null>(null);
  const [hover, setHover] = useState<string | null>(null);
  // Right side holds ONE panel at a time: node drawer or path chat.
  const [chatOpen, setChatOpen] = useState(false);
  const [chatSeed, setChatSeed] = useState<{ text: string; nonce: number } | null>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [view, setView] = useState({ k: 1, x: 0, y: 0 });
  const vpRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef(view);
  viewRef.current = view;
  const fittedRef = useRef(false);

  const fitView = (w: number, h: number) => {
    const k = clamp(Math.min(w / world.w, h / world.h) * 0.94, 0.3, 1.2);
    return { k, x: (w - world.w * k) / 2, y: (h - world.h * k) / 2 + 6 };
  };

  // viewport size tracking; fit once on first measure
  useEffect(() => {
    const el = vpRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0].contentRect;
      setSize({ w: r.width, h: r.height });
      if (!fittedRef.current && r.width > 0) {
        fittedRef.current = true;
        setView(fitView(r.width, r.height));
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // wheel zoom (must be non-passive to preventDefault)
  useEffect(() => {
    const el = vpRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      setView((v) => {
        const k2 = clamp(v.k * Math.exp(-e.deltaY * 0.0014), 0.32, 1.9);
        const s = k2 / v.k;
        return { k: k2, x: mx - (mx - v.x) * s, y: my - (my - v.y) * s };
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  // escape closes the drawer
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setSelected(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const isLinked = (id: string) =>
    hover !== null && edges.some(([a, b]) => (a === hover && b === id) || (b === hover && a === id));

  // Unified pan + pinch on the viewport: one pointer drags, two pointers
  // pinch-zoom (mobile). Mouse and touch share the same path; a tap that
  // never moves deselects, matching the old click-to-clear behavior.
  useEffect(() => {
    const el = vpRef.current;
    if (!el) return;
    const pts = new Map<number, { x: number; y: number }>();
    let panStart: { x: number; y: number; vx: number; vy: number } | null = null;
    let pinchStart: { d: number; cx: number; cy: number; k: number; x: number; y: number } | null = null;
    let moved = false;

    const onDown = (e: PointerEvent) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
      el.setPointerCapture(e.pointerId);
      if (pts.size === 1) {
        moved = false;
        panStart = { x: e.clientX, y: e.clientY, vx: viewRef.current.x, vy: viewRef.current.y };
        pinchStart = null;
      } else if (pts.size === 2) {
        const [a, b] = Array.from(pts.values());
        const rect = el.getBoundingClientRect();
        pinchStart = {
          d: Math.hypot(a.x - b.x, a.y - b.y),
          cx: (a.x + b.x) / 2 - rect.left,
          cy: (a.y + b.y) / 2 - rect.top,
          k: viewRef.current.k,
          x: viewRef.current.x,
          y: viewRef.current.y,
        };
        panStart = null;
      }
    };
    const onMove = (e: PointerEvent) => {
      if (!pts.has(e.pointerId)) return;
      pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pinchStart && pts.size >= 2) {
        const [a, b] = Array.from(pts.values());
        const d = Math.hypot(a.x - b.x, a.y - b.y);
        if (pinchStart.d < 1) return;
        const k2 = clamp(pinchStart.k * (d / pinchStart.d), 0.32, 1.9);
        const s = k2 / pinchStart.k;
        moved = true;
        setView({
          k: k2,
          x: pinchStart.cx - (pinchStart.cx - pinchStart.x) * s,
          y: pinchStart.cy - (pinchStart.cy - pinchStart.y) * s,
        });
      } else if (panStart) {
        const dx = e.clientX - panStart.x;
        const dy = e.clientY - panStart.y;
        if (Math.abs(dx) + Math.abs(dy) > 4) moved = true;
        setView((v) => ({ ...v, x: panStart!.vx + dx, y: panStart!.vy + dy }));
      }
    };
    const onUp = (e: PointerEvent) => {
      if (!pts.delete(e.pointerId)) return;
      if (pts.size < 2) pinchStart = null;
      if (pts.size === 1) {
        const [p] = Array.from(pts.values());
        panStart = { x: p.x, y: p.y, vx: viewRef.current.x, vy: viewRef.current.y };
      } else if (pts.size === 0) {
        if (!moved) setSelected(null);
        panStart = null;
      }
    };
    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointercancel', onUp);
    return () => {
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onUp);
    };
  }, []);

  const zoomBy = (f: number) => {
    setView((v) => {
      const k2 = clamp(v.k * f, 0.32, 1.9);
      const s = k2 / v.k;
      const cx = size.w / 2;
      const cy = size.h / 2;
      return { k: k2, x: cx - (cx - v.x) * s, y: cy - (cy - v.y) * s };
    });
  };

  const centerOn = (id: string) => {
    const p = byId[id];
    if (!p) return;
    setView((v) => {
      const k = Math.max(v.k, 0.85);
      return { k, x: size.w / 2 - p.x * k, y: size.h / 2 - p.y * k };
    });
  };

  // Selecting a node closes chat (and vice versa) — one right panel at a time.
  const selectNode = (id: string) => { setChatOpen(false); setSelected(id); };

  const jumpTo = (id: string) => { selectNode(id); centerOn(id); };

  // Open path chat, optionally prefilled with a canned message (the mockup's
  // Skip / Try-another-way buttons became canned chat messages by design).
  const openChat = (seedText?: string) => {
    setSelected(null);
    if (seedText) setChatSeed({ text: seedText, nonce: Date.now() });
    setChatOpen(true);
  };

  const start = (node: PathNode) => {
    if (isStarter && node.surface === 'capstone') {
      navigate('/pay?reason=capstone_requires_pro');
      return;
    }
    navigate(deepLink(path.path_id, node));
  };

  // curved edge between two node centers (always left → right)
  const edgePath = (a: string, b: string): string => {
    const A = byId[a];
    const B = byId[b];
    const x1 = A.x + A.r + 4;
    const y1 = A.y;
    const x2 = B.x - B.r - 12;
    const y2 = B.y;
    const dx = Math.max(70, (x2 - x1) * 0.5);
    return `M${x1},${y1} C${x1 + dx},${y1} ${x2 - dx},${y2} ${x2},${y2}`;
  };

  // done = travelled (green) · learn = the animated edge into the suggested
  // next step · open = everything else. No 'faint' tier: on a fresh path all
  // edges would be faint, which reads as "no edges at all" on the dark bg.
  const edgeClass = (a: string, b: string): string => {
    const sa = statuses[a];
    const sb = statuses[b];
    if (sa === 'completed' && sb === 'completed') return 'done';
    if (sa === 'completed' && b === nextId) return 'learn';
    return 'open';
  };

  /* derived header stats */
  const done = path.nodes.filter((n) => statuses[n.id] === 'completed').length;
  const pct = path.nodes.length ? Math.round((done / path.nodes.length) * 100) : 0;

  const sel = selected ? nodeById.get(selected) ?? null : null;
  const selStatus = sel ? statuses[sel.id] : null;
  const selMeta = sel ? SURFACE_META[sel.surface] : null;
  const selColors = sel
    ? sel.level === 'project' && selStatus === 'available'
      ? { c: PROJ_C, g: PROJ_G }
      : { c: ST[selStatus!].c, g: ST[selStatus!].g }
    : null;

  /* minimap */
  const MM_W = 200;
  const MM_S = MM_W / world.w;
  const MM_H = Math.round(world.h * MM_S);
  const mmr = {
    x: clamp((-view.x / view.k) * MM_S, 0, MM_W),
    y: clamp((-view.y / view.k) * MM_S, 0, MM_H),
    w: 0,
    h: 0,
  };
  mmr.w = clamp((size.w / view.k) * MM_S, 8, MM_W - mmr.x);
  mmr.h = clamp((size.h / view.k) * MM_S, 8, MM_H - mmr.y);

  const mmClick = (e: React.MouseEvent<SVGSVGElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    const wx = (e.clientX - r.left) / MM_S;
    const wy = (e.clientY - r.top) / MM_S;
    setView((v) => ({ ...v, x: size.w / 2 - wx * v.k, y: size.h / 2 - wy * v.k }));
  };

  return (
    <div className="pathgraph">
      {/* HEADER */}
      <div className="pg-header">
        <div className="brand">
          <div className="logo">🧭</div>
          <div>
            <div className="bt" title={path.topic}><em>{path.topic}</em></div>
            <div className="bs">Learning Path · {path.duration_hours}h</div>
          </div>
        </div>
        <div className="prog">
          <div className="pt">
            <span>{done} of {path.nodes.length} steps</span>
            <span>{pct}%</span>
          </div>
          <div className="bar"><div className="fill" style={{ width: pct + '%' }}></div></div>
        </div>
        <div className="hright">
          <button
            className={`btn sm ${chatOpen ? 'primary' : 'ghost'}`}
            onClick={() => (chatOpen ? setChatOpen(false) : openChat())}
          >
            💬 Path chat
          </button>
          <button className="btn ghost sm" onClick={() => navigate('/paths')}>← My paths</button>
        </div>
      </div>

      {/* GRAPH VIEWPORT */}
      <div className="viewport" ref={vpRef}>
        <div
          className="world"
          style={{
            width: world.w,
            height: world.h,
            transform: `translate(${view.x}px, ${view.y}px) scale(${view.k})`,
          }}
        >
          {/* lane labels + guides */}
          {lanes.map((l, i) => (
            <React.Fragment key={i}>
              <div className="lane" style={{ left: l.x }}>{l.label}</div>
              <div className="lane-line" style={{ left: l.x }}></div>
            </React.Fragment>
          ))}

          {/* edges */}
          <svg className="edges" width={world.w} height={world.h}>
            <defs>
              <marker id="pg-m-done" viewBox="0 0 10 10" refX="8.5" refY="5" markerWidth="6.5" markerHeight="6.5" orient="auto-start-reverse"><path d="M0,0 L10,5 L0,10 z" fill="#34d399" /></marker>
              <marker id="pg-m-learn" viewBox="0 0 10 10" refX="8.5" refY="5" markerWidth="6.5" markerHeight="6.5" orient="auto-start-reverse"><path d="M0,0 L10,5 L0,10 z" fill="#fbbf24" /></marker>
              <marker id="pg-m-open" viewBox="0 0 10 10" refX="8.5" refY="5" markerWidth="6.5" markerHeight="6.5" orient="auto-start-reverse"><path d="M0,0 L10,5 L0,10 z" fill="#8598ba" /></marker>
              <marker id="pg-m-faint" viewBox="0 0 10 10" refX="8.5" refY="5" markerWidth="6.5" markerHeight="6.5" orient="auto-start-reverse"><path d="M0,0 L10,5 L0,10 z" fill="#3c4a68" /></marker>
            </defs>
            {edges.map(([a, b], i) => {
              const cls = edgeClass(a, b);
              const linked = hover !== null && (a === hover || b === hover);
              return (
                <path
                  key={i}
                  d={edgePath(a, b)}
                  className={`edge e-${cls}${linked ? ' linked' : ''}`}
                  markerEnd={`url(#pg-m-${cls})`}
                />
              );
            })}
          </svg>

          {/* nodes */}
          {positioned.map((p) => {
            const n = p.node;
            const st = statuses[n.id];
            const isProj = n.level === 'project';
            const projReady = isProj && st === 'available';
            const col = projReady ? PROJ_C : ST[st].c;
            const glow = projReady ? PROJ_G : ST[st].g;
            const cls = [
              'node',
              'st-' + st,
              isProj && 'proj',
              selected === n.id && 'sel',
              hover && hover !== n.id && !isLinked(n.id) && 'dim',
            ].filter(Boolean).join(' ');
            return (
              <div
                key={n.id}
                className={cls}
                style={{ left: p.x - p.r, top: p.y - p.r, '--sc': col, '--sg': glow } as React.CSSProperties}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={() => selectNode(n.id)}
                onMouseEnter={() => setHover(n.id)}
                onMouseLeave={() => setHover(null)}
              >
                {n.id === nextId && !isProj && <span className="pulse"></span>}
                {projReady && <span className="pulse violet"></span>}
                <div className="ncircle"><span>{SURFACE_META[n.surface].icon}</span></div>
                {st === 'completed' && <span className="badge b-done">{ICONS.check}</span>}
                <div className="nlabel">
                  <div className="ntitle">{n.title}</div>
                  <div className="nmeta">⏱ {formatMinutes(n.est_minutes)} · {SURFACE_META[n.surface].label}</div>
                </div>
              </div>
            );
          })}

          <div className="watermark">
            {path.topic.toUpperCase()} · {path.nodes.length} STEPS · {edges.length} EDGES
          </div>
        </div>
      </div>

      {/* LEGEND */}
      <div className="legend">
        <span><i className="dot done" />Completed</span>
        <span><i className="dot avail" />To learn</span>
      </div>

      {/* ZOOM CONTROLS */}
      <div className="controls">
        <button onClick={() => zoomBy(1.28)} title="Zoom in">+</button>
        <button onClick={() => zoomBy(0.78)} title="Zoom out">−</button>
        <button onClick={() => setView(fitView(size.w, size.h))} title="Fit graph">⤢</button>
      </div>

      {/* HINT */}
      <div className="hint">Drag to pan · Scroll to zoom · Click a node for details</div>

      {/* MINIMAP */}
      <div className="minimap" style={{ right: sel || chatOpen ? 424 : 20 }}>
        <div className="mm-title">Graph Overview</div>
        <svg width={MM_W} height={MM_H} onClick={mmClick}>
          {edges.map(([a, b], i) => {
            const A = byId[a];
            const B = byId[b];
            return (
              <line
                key={i}
                x1={A.x * MM_S} y1={A.y * MM_S} x2={B.x * MM_S} y2={B.y * MM_S}
                stroke="rgba(148,163,184,.18)" strokeWidth="1"
              />
            );
          })}
          {positioned.map((p) => {
            const st = statuses[p.node.id];
            const fill = ST[st].c;
            return (
              <circle
                key={p.node.id}
                cx={p.x * MM_S} cy={p.y * MM_S}
                r={p.node.level === 'project' ? 4 : 2.6}
                fill={fill}
              />
            );
          })}
          <rect x={mmr.x} y={mmr.y} width={mmr.w} height={mmr.h} rx="4" fill="rgba(125,211,252,.08)" stroke="#7dd3fc" strokeWidth="1.2" />
        </svg>
      </div>

      {/* DETAIL DRAWER — Start → launches the step; status changes go
          through path chat via the canned-message buttons below */}
      {sel && selStatus && selMeta && selColors && (
        <aside className="drawer" key={sel.id}>
          <button className="dx" onClick={() => setSelected(null)}>{ICONS.close}</button>
          <div className="d-head">
            <div className="d-icon" style={{ '--sc': selColors.c, '--sg': selColors.g } as React.CSSProperties}>
              {selMeta.icon}
            </div>
            <div>
              <div className="d-title">{sel.title}</div>
              <div className="d-lvl">{LEVEL_TITLE[sel.level]} · {selMeta.label}</div>
            </div>
          </div>

          <div>
            <span
              className="pill"
              style={{
                background: selColors.c + '1f',
                color: selColors.c,
                border: '1px solid ' + selColors.c + '55',
              }}
            >
              <i style={{ background: selColors.c }} />
              {ST[selStatus].label}
            </span>
          </div>

          <div className="d-meta">
            <span className="chip">⏱ {formatMinutes(sel.est_minutes)}</span>
            <span className="chip">{selMeta.icon} {selMeta.label}</span>
            {sel.upgradable_to_capstone && <span className="chip">🏗️ Capstone-ready later</span>}
            {sel.branch_of && <span className="chip">↩ alternate route</span>}
          </div>

          <div className="d-sec">What you'll do</div>
          <p className="d-desc">{sel.description || sel.prompt}</p>

          {sel.depends_on.length > 0 && (
            <React.Fragment>
              <div className="d-sec">Builds on</div>
              <div className="preqs">
                {sel.depends_on.filter((p) => nodeById.has(p)).map((p) => (
                  <span key={p} className="preq" onClick={() => jumpTo(p)}>
                    <i style={{ background: ST[statuses[p]].c }} />
                    {nodeById.get(p)!.title}
                  </span>
                ))}
              </div>
            </React.Fragment>
          )}

          {(outgoing[sel.id] || []).length > 0 && (
            <React.Fragment>
              <div className="d-sec">Leads to</div>
              <div className="preqs">
                {outgoing[sel.id].map((t) => (
                  <span key={t} className="preq" onClick={() => jumpTo(t)}>
                    <i style={{ background: ST[statuses[t]].c }} />
                    {nodeById.get(t)!.title}
                  </span>
                ))}
              </div>
            </React.Fragment>
          )}

          <div className="acts">
            {selStatus === 'available' && (
              isStarter && sel.surface === 'capstone' ? (
                <button className="btn amber" onClick={() => navigate('/pay?reason=capstone_requires_pro')}>
                  🔒 Upgrade to unlock Capstone
                </button>
              ) : (
                <button className="btn amber" onClick={() => start(sel)}>
                  ▶ Start {sel.level === 'project' ? 'project' : 'step'} in {selMeta.label}
                </button>
              )
            )}
            {selStatus === 'completed' && (
              <button className="btn ghost" onClick={() => start(sel)}>↻ Revisit in {selMeta.label}</button>
            )}
            {/* Mockup's Skip / Try-another-way as canned chat messages —
                chat is the only mutation channel (step 4). */}
            {selStatus === 'available' && (
              <React.Fragment>
                <button
                  className="btn ghost"
                  onClick={() => openChat(`I'm stuck on "${sel.title}" — give me another way to learn it.`)}
                >
                  💡 Another way
                </button>
                <button
                  className="btn ghost"
                  onClick={() => openChat(`Mark "${sel.title}" as done — I've already covered it elsewhere.`)}
                >
                  ✓ Mark as done
                </button>
              </React.Fragment>
            )}
          </div>
        </aside>
      )}

      {/* PATH CHAT — the only graph mutation channel (step 4) */}
      {chatOpen && (
        <PathChatPanel
          pathId={path.path_id}
          seed={chatSeed}
          onClose={() => setChatOpen(false)}
          onPathUpdate={onPathUpdate}
        />
      )}
    </div>
  );
};

export default PathGraph;
